import { useEffect, useRef, useState } from "react";
import { useSearchParams, useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuctionCard from "@/components/AuctionCard";
import { toast } from "sonner";
import {
  FunnelSimple, X, Sparkle, Lightning, Star, MagnifyingGlass, ShieldCheck,
  CaretRight, ArrowClockwise, Package,
} from "@phosphor-icons/react";

const DOPING_OPTIONS = [
  { id: "", label: "Hepsi" },
  { id: "homepage_vitrin", label: "Ana Sayfa Vitrin" },
  { id: "kategori_vitrin", label: "Kategori Vitrin" },
  { id: "top_row", label: "Üst Sıra" },
  { id: "bold_title", label: "Kalın Başlık" },
  { id: "urgent_badge", label: "Acil Rozeti" },
];

const SORT_OPTIONS = [
  { id: "ending_soon", label: "Bitmek üzere" },
  { id: "most_bids", label: "En çok teklif" },
  { id: "newest", label: "En yeni" },
  { id: "price_asc", label: "Fiyat: artan" },
  { id: "price_desc", label: "Fiyat: azalan" },
];

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const routeParams = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [landing, setLanding] = useState(null);
  const [favSet, setFavSet] = useState(new Set());
  const [searchDraft, setSearchDraft] = useState("");
  const minRef = useRef(null);
  const maxRef = useRef(null);

  useEffect(() => {
    if (!user) { setFavSet(new Set()); return; }
    api.get("/me/favorites")
      .then(({ data }) => setFavSet(new Set(data.map((f) => f.id))))
      .catch(() => {});
  }, [user]);

  const toggleFav = async (auctionId) => {
    if (!user) { toast.error("Favori için giriş yapın"); nav("/giris"); return; }
    const isFav = favSet.has(auctionId);
    const next = new Set(favSet);
    if (isFav) next.delete(auctionId); else next.add(auctionId);
    setFavSet(next);
    try {
      if (isFav) await api.delete(`/favorites/${auctionId}`);
      else await api.post(`/favorites/${auctionId}`);
    } catch {
      setFavSet(favSet);
      toast.error("Favori güncellenemedi");
    }
  };

  // Route disambiguation
  const isCityOnlyRoute = location.pathname.startsWith("/kesfet/sehir/");
  const isSubcategoryRoute = location.pathname.includes("/alt/");
  const hasBothSegments = Boolean(routeParams.city && routeParams.category && !isSubcategoryRoute);
  const effectiveRouteCity = isCityOnlyRoute ? routeParams.city
    : hasBothSegments ? routeParams.city : "";
  const effectiveRouteCategory = isCityOnlyRoute ? ""
    : isSubcategoryRoute ? routeParams.category
    : hasBothSegments ? routeParams.category
    : routeParams.category || "";
  const effectiveRouteSubcategory = isSubcategoryRoute ? routeParams.subcategory : "";

  const q = params.get("q") || "";
  const category = effectiveRouteCategory || params.get("category") || "";
  const subcategory = effectiveRouteSubcategory || params.get("subcategory") || "";
  const sort = params.get("sort") || "ending_soon";
  const status = params.get("status") || "active";
  const city = landing?.canonical_city || effectiveRouteCity || params.get("city") || "";
  const condition = params.get("condition") || "";
  const shipping_type = params.get("shipping_type") || "";
  const listing_type = params.get("listing_type") || "";
  const doping = params.get("doping") || "";
  const min_price = params.get("min_price") || "";
  const max_price = params.get("max_price") || "";
  const doping_only = params.get("doping_only") || "";
  const ending_soon = params.get("ending_soon") || "";

  useEffect(() => { setSearchDraft(q); }, [q]);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/cities")]).then(([c, ci]) => {
      setCategories(c.data); setCities(ci.data);
    });
  }, []);

  useEffect(() => {
    if (!effectiveRouteCity && !effectiveRouteCategory && !effectiveRouteSubcategory) {
      setLanding(null);
      return;
    }
    api.get("/seo/landing", {
      params: {
        city: effectiveRouteCity || undefined,
        category: effectiveRouteCategory || undefined,
        subcategory: effectiveRouteSubcategory || undefined,
      },
    }).then(({ data }) => {
      setLanding(data);
      if (data.title) document.title = data.title;
    }).catch(() => setLanding(null));
  }, [effectiveRouteCity, effectiveRouteCategory, effectiveRouteSubcategory]);

  useEffect(() => {
    setLoading(true);
    api.get("/auctions", {
      params: {
        q: q || undefined, category: category || undefined,
        subcategory: subcategory || undefined,
        sort, status,
        city: city || undefined, condition: condition || undefined,
        shipping_type: shipping_type || undefined, listing_type: listing_type || undefined,
        doping: doping || undefined,
        min_price: min_price ? Number(min_price) : undefined,
        max_price: max_price ? Number(max_price) : undefined,
        doping_only: doping_only === "1" || undefined,
        ending_soon: ending_soon === "1" || undefined,
        limit: 100,
      },
    })
      .then(({ data }) => { setAuctions(data); setLoadError(null); })
      .catch((e) => setLoadError(e.response?.data?.detail || "İlanlar yüklenemedi. Lütfen tekrar deneyin."))
      .finally(() => setLoading(false));
  }, [q, category, subcategory, sort, status, city, condition, shipping_type, listing_type,
      doping, min_price, max_price, doping_only, ending_soon]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const clearAll = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    setParams(p);
  };

  const clearAllIncludingSearch = () => setParams(new URLSearchParams());

  const submitSearch = (e) => {
    e?.preventDefault?.();
    update("q", searchDraft.trim());
  };

  // Active filter chips
  const chips = [];
  if (category && !effectiveRouteCategory) {
    const c = categories.find((x) => x.id === category);
    chips.push({ k: "category", label: c?.name || category });
  }
  if (city && !effectiveRouteCity && !landing?.canonical_city) chips.push({ k: "city", label: city });
  if (condition) chips.push({ k: "condition", label: condition === "new" ? "Sıfır" : "İkinci el" });
  if (shipping_type) chips.push({ k: "shipping_type", label: shipping_type === "kargo" ? "Kargo" : shipping_type === "dahil" ? "Kargo dahil" : "Elden" });
  if (listing_type) chips.push({ k: "listing_type", label: listing_type === "auction" ? "Açık artırma" : "Süresiz" });
  if (doping) {
    const d = DOPING_OPTIONS.find((x) => x.id === doping);
    chips.push({ k: "doping", label: `Doping: ${d?.label}` });
  }
  if (doping_only === "1") chips.push({ k: "doping_only", label: "Sadece dopingli" });
  if (ending_soon === "1") chips.push({ k: "ending_soon", label: "24s içinde biten" });
  if (min_price) chips.push({ k: "min_price", label: `Min ₺${Number(min_price).toLocaleString("tr-TR")}` });
  if (max_price) chips.push({ k: "max_price", label: `Max ₺${Number(max_price).toLocaleString("tr-TR")}` });
  if (status && status !== "active") chips.push({ k: "status", label: status === "closed" ? "Sona ermiş" : "Tüm durumlar" });

  // Context header
  const activeCategory = categories.find((c) => c.id === category);
  const activeSubcat = activeCategory?.subcategories?.find((s) => s.id === subcategory);
  const displayCity = landing?.canonical_city || city;
  const heroEyebrow = displayCity && activeCategory ? `${displayCity} · ${activeCategory.name}`
    : displayCity ? "Şehir"
    : activeCategory ? "Kategori"
    : "Keşfet";
  const heroTitle = landing?.heading
    || (q ? `"${q}" için sonuçlar`
      : activeSubcat ? activeSubcat.name
      : activeCategory ? activeCategory.name
      : displayCity ? `${displayCity} ilanları`
      : "Tüm ilanlar");

  // ─── Filter groups (shared desktop + mobile) ─────────────────────────
  const FilterContent = (
    <>
      <FilterGroup label="Durum">
        {[
          { id: "active", label: "Aktif" },
          { id: "closed", label: "Sona ermiş" },
          { id: "all", label: "Hepsi" },
        ].map((s) => (
          <FilterBtn key={s.id} active={status === s.id} onClick={() => update("status", s.id)} testId={`filter-status-${s.id}`}>{s.label}</FilterBtn>
        ))}
      </FilterGroup>

      <FilterGroup label="Hızlı seç">
        <FilterBtn active={ending_soon === "1"} onClick={() => update("ending_soon", ending_soon === "1" ? "" : "1")} testId="filter-ending-soon">
          <Lightning size={12} weight="fill" className="inline text-[#B54037] mr-1" /> 24 saat içinde bitiyor
        </FilterBtn>
        <FilterBtn active={doping_only === "1"} onClick={() => update("doping_only", doping_only === "1" ? "" : "1")} testId="filter-doping-only">
          <Star size={12} weight="fill" className="inline text-amber-500 mr-1" /> Sadece öne çıkan
        </FilterBtn>
      </FilterGroup>

      <FilterGroup label="İlan tipi">
        <FilterBtn active={!listing_type} onClick={() => update("listing_type", "")}>Tümü</FilterBtn>
        <FilterBtn active={listing_type === "auction"} onClick={() => update("listing_type", "auction")} testId="filter-lt-auction">Açık artırma</FilterBtn>
        <FilterBtn active={listing_type === "open_ended"} onClick={() => update("listing_type", "open_ended")} testId="filter-lt-openended">Hemen Al (süresiz)</FilterBtn>
      </FilterGroup>

      <FilterGroup label="Fiyat aralığı (₺)">
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="0" placeholder="Min" defaultValue={min_price}
            data-testid="filter-min-price"
            onChange={(e) => { clearTimeout(minRef.current); minRef.current = setTimeout(() => update("min_price", e.target.value), 500); }}
            className="bg-white premium-border rounded-lg px-3 py-2 text-sm font-mono-tight focus:outline-none focus:border-slate-900 transition-colors" />
          <input type="number" min="0" placeholder="Max" defaultValue={max_price}
            data-testid="filter-max-price"
            onChange={(e) => { clearTimeout(maxRef.current); maxRef.current = setTimeout(() => update("max_price", e.target.value), 500); }}
            className="bg-white premium-border rounded-lg px-3 py-2 text-sm font-mono-tight focus:outline-none focus:border-slate-900 transition-colors" />
        </div>
      </FilterGroup>

      <FilterGroup label="İl">
        <select className="w-full bg-white premium-border rounded-lg px-3 py-2.5 text-sm text-slate-800 hover:border-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
          value={city} onChange={(e) => update("city", e.target.value)}
          disabled={!!landing?.canonical_city} data-testid="filter-city">
          <option value="">Tüm iller</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </FilterGroup>

      <FilterGroup label="Ürün durumu">
        <FilterBtn active={!condition} onClick={() => update("condition", "")}>Hepsi</FilterBtn>
        <FilterBtn active={condition === "new"} onClick={() => update("condition", "new")} testId="filter-cond-new">Sıfır</FilterBtn>
        <FilterBtn active={condition === "used"} onClick={() => update("condition", "used")} testId="filter-cond-used">İkinci el</FilterBtn>
      </FilterGroup>

      <FilterGroup label="Kargo">
        <FilterBtn active={!shipping_type} onClick={() => update("shipping_type", "")}>Hepsi</FilterBtn>
        <FilterBtn active={shipping_type === "kargo"} onClick={() => update("shipping_type", "kargo")} testId="filter-ship-kargo">Kargo</FilterBtn>
        <FilterBtn active={shipping_type === "dahil"} onClick={() => update("shipping_type", "dahil")} testId="filter-ship-dahil">Kargo dahil</FilterBtn>
        <FilterBtn active={shipping_type === "elden"} onClick={() => update("shipping_type", "elden")} testId="filter-ship-elden">Elden</FilterBtn>
      </FilterGroup>

      {!effectiveRouteCategory && (
        <FilterGroup label="Kategori">
          <FilterBtn active={!category} onClick={() => update("category", "")}>Tümü</FilterBtn>
          {categories.map((c) => (
            <FilterBtn key={c.id} active={category === c.id} onClick={() => update("category", c.id)} testId={`filter-cat-${c.id}`}>{c.name}</FilterBtn>
          ))}
        </FilterGroup>
      )}

      <FilterGroup label="Öne çıkarma tipi">
        {DOPING_OPTIONS.map((d) => (
          <FilterBtn key={d.id || "any"} active={doping === d.id} onClick={() => update("doping", d.id)} testId={`filter-doping-${d.id || "all"}`}>
            {d.label}
          </FilterBtn>
        ))}
      </FilterGroup>

      {chips.length > 0 && (
        <button onClick={clearAll} data-testid="clear-filters-button"
          className="w-full text-xs font-medium text-[#B54037] hover:bg-red-50 py-2.5 rounded-lg transition-colors">
          Tüm filtreleri temizle
        </button>
      )}
    </>
  );

  return (
    <div className="max-w-[1280px] mx-auto px-5 md:px-6 py-8 md:py-12" data-testid="explore-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-4" data-testid="explore-breadcrumb">
        <Link to="/" className="hover:text-slate-900 transition-colors">Ana Sayfa</Link>
        <CaretRight size={9} weight="bold" className="text-slate-300" />
        <Link to="/kesfet" className={activeCategory || displayCity ? "hover:text-slate-900 transition-colors" : "text-slate-900 font-medium"}>Keşfet</Link>
        {displayCity && (
          <>
            <CaretRight size={9} weight="bold" className="text-slate-300" />
            <span className={activeCategory ? "hover:text-slate-900 transition-colors" : "text-slate-900 font-medium"}>{displayCity}</span>
          </>
        )}
        {activeCategory && (
          <>
            <CaretRight size={9} weight="bold" className="text-slate-300" />
            <span className={activeSubcat ? "hover:text-slate-900 transition-colors" : "text-slate-900 font-medium"}>{activeCategory.name}</span>
          </>
        )}
        {activeSubcat && (
          <>
            <CaretRight size={9} weight="bold" className="text-slate-300" />
            <span className="text-slate-900 font-medium">{activeSubcat.name}</span>
          </>
        )}
      </nav>

      {/* Explore hero — H1 + subtitle + count */}
      <header className="mb-8" data-testid="explore-header">
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">{heroEyebrow}</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900 leading-[1.05]">
          {heroTitle}
        </h1>
        {landing?.description ? (
          <p className="mt-4 text-base text-slate-500 leading-relaxed max-w-2xl" data-testid="seo-meta-desc">
            {landing.description}
          </p>
        ) : (
          <p className="mt-4 text-sm text-slate-500 flex items-center gap-2 flex-wrap">
            <ShieldCheck size={13} weight="fill" className="text-emerald-700" />
            Doğrulanmış satıcılar · AI onaylı ilanlar · Havuz hesap güvencesi
          </p>
        )}
      </header>

      {/* Explore search bar — pill */}
      <form
        onSubmit={submitSearch}
        className="flex items-center bg-white premium-border rounded-full soft-shadow mb-6 md:mb-8 max-w-2xl"
        data-testid="explore-search-form"
      >
        <MagnifyingGlass size={16} className="ml-5 text-slate-400" />
        <input
          data-testid="explore-search-input"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder="iPhone, villa, araba, koltuk ara..."
          className="flex-1 bg-transparent px-3 py-3 outline-none text-sm md:text-base placeholder:text-slate-400 text-slate-900"
        />
        {searchDraft && (
          <button type="button" onClick={() => { setSearchDraft(""); update("q", ""); }} className="mr-1 p-2 rounded-full hover:bg-slate-100 text-slate-400" aria-label="Aramayı temizle" data-testid="explore-search-clear">
            <X size={13} weight="bold" />
          </button>
        )}
        <button type="submit" className="bg-slate-900 text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-black transition-colors m-1" data-testid="explore-search-submit">
          Ara
        </button>
      </form>

      {/* Subcategory quick links — when category is selected but no subcategory yet */}
      {activeCategory && activeCategory.subcategories?.length > 0 && !activeSubcat && (
        <div className="mb-6 md:mb-8" data-testid="subcategory-quicklinks">
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Alt kategoriler</p>
          <div className="flex flex-wrap gap-2 overflow-x-auto pb-1">
            {activeCategory.subcategories.slice(0, 12).map((s) => (
              <Link
                key={s.id}
                to={`/kesfet/${activeCategory.id}/alt/${s.id}`}
                data-testid={`category-quick-link-${s.id}`}
                className="px-4 py-2 rounded-full bg-white premium-border text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-colors whitespace-nowrap"
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile filter + sort bar */}
      <div className="md:hidden mb-5 flex items-center justify-between gap-3">
        <button onClick={() => setMobileFiltersOpen(true)} data-testid="mobile-filter-button"
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-full font-medium text-sm">
          <FunnelSimple size={14} weight="bold" /> Filtreler {chips.length > 0 && <span className="bg-white/20 rounded-full px-2 py-0.5 text-[11px]">{chips.length}</span>}
        </button>
        <SortDropdown value={sort} onChange={(v) => update("sort", v)} compact />
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 md:mb-8" data-testid="active-filter-chips">
          {chips.map((c) => (
            <button
              key={c.k}
              onClick={() => update(c.k, "")}
              data-testid={`active-filter-chip-${c.k}`}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-medium pl-3 pr-2 py-1.5 rounded-full hover:bg-black transition-colors"
            >
              {c.label}
              <X size={11} weight="bold" />
            </button>
          ))}
          <button onClick={clearAll} className="text-xs font-medium text-slate-500 hover:text-slate-900 px-2 py-1.5" data-testid="chip-clear-all">
            Temizle
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
        {/* Desktop filter sidebar */}
        <aside className="hidden md:block md:col-span-3" data-testid="explore-filter-sidebar">
          <div className="sticky top-24 rounded-2xl bg-white premium-border p-5 space-y-7">
            <div className="flex items-center justify-between">
              <p className="font-display text-base font-medium tracking-tight text-slate-900">Filtreler</p>
              {chips.length > 0 && <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">{chips.length} aktif</span>}
            </div>
            {FilterContent}
          </div>
        </aside>

        {/* Mobile drawer */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-filter-drawer">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-white flex flex-col rounded-l-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b divider">
                <p className="font-display text-lg font-medium tracking-tight text-slate-900">Filtreler</p>
                <button onClick={() => setMobileFiltersOpen(false)} data-testid="mobile-filter-close" className="p-2 rounded-full hover:bg-slate-50">
                  <X size={16} weight="bold" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-7">
                {FilterContent}
              </div>
              <div className="p-4 border-t divider flex gap-2">
                <button onClick={() => { clearAll(); }} data-testid="mobile-filter-clear" className="flex-1 border border-slate-200 text-slate-700 rounded-full py-3 font-medium text-sm hover:border-slate-900 transition-colors">
                  Temizle
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  data-testid="mobile-filter-apply"
                  className="flex-1 bg-slate-900 text-white py-3 rounded-full font-medium text-sm hover:bg-black transition-colors"
                >
                  Göster ({auctions.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results column */}
        <div className="md:col-span-9 min-w-0">
          {/* Desktop results header — count + sort */}
          <div className="hidden md:flex items-center justify-between mb-6" data-testid="results-header">
            <p className="text-sm text-slate-500" data-testid="results-count">
              {loading ? "Yükleniyor..." : `${auctions.length} ilan bulundu`}
            </p>
            <SortDropdown value={sort} onChange={(v) => update("sort", v)} />
          </div>

          {loading ? (
            <ListingSkeletonGrid />
          ) : loadError ? (
            <ErrorState onRetry={() => window.location.reload()} message={loadError} />
          ) : auctions.length === 0 ? (
            <EmptyResults hasFilters={chips.length > 0 || Boolean(q)} onClearFilters={clearAllIncludingSearch} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 md:gap-7" data-testid="results-grid">
              {auctions.map((a) => (
                <div key={a.id} data-testid="listing-card">
                  <AuctionCard
                    auction={a}
                    onFav={toggleFav}
                    isFav={favSet.has(a.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────

function SortDropdown({ value, onChange, compact = false }) {
  return (
    <div className={`relative ${compact ? "" : "w-56"}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={compact ? "sort-dropdown-mobile" : "sort-dropdown"}
        className={`w-full bg-white premium-border rounded-full px-4 pr-9 text-sm font-medium text-slate-800 hover:border-slate-900 focus:outline-none focus:border-slate-900 transition-colors appearance-none ${compact ? "py-2" : "py-2.5"}`}
      >
        {SORT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <CaretRight size={11} weight="bold" className="absolute right-3.5 top-1/2 -translate-y-1/2 rotate-90 text-slate-400 pointer-events-none" />
    </div>
  );
}

function ListingSkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7" data-testid="listing-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-2xl bg-white premium-border overflow-hidden animate-pulse">
          <div className="aspect-[5/4] bg-slate-100" />
          <div className="p-5 space-y-3">
            <div className="h-2 w-16 bg-slate-100 rounded" />
            <div className="h-4 w-full bg-slate-100 rounded" />
            <div className="h-4 w-3/4 bg-slate-100 rounded" />
            <div className="pt-4 border-t divider flex justify-between">
              <div className="h-4 w-20 bg-slate-100 rounded" />
              <div className="h-4 w-12 bg-slate-100 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyResults({ hasFilters, onClearFilters }) {
  return (
    <div className="rounded-2xl bg-white premium-border py-20 px-6 text-center" data-testid="empty-results">
      <div className="w-14 h-14 mx-auto rounded-full bg-slate-50 flex items-center justify-center mb-5">
        <Package size={22} weight="regular" className="text-slate-500" />
      </div>
      <h3 className="font-display text-xl font-medium tracking-tight text-slate-900 mb-2">Bu kriterlere uygun ilan bulunamadı.</h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6 leading-relaxed">
        Filtreleri azaltarak veya farklı bir arama yaparak tekrar deneyin.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        {hasFilters && (
          <button onClick={onClearFilters} data-testid="empty-clear-filters"
            className="bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-black transition-colors">
            Filtreleri temizle
          </button>
        )}
        <Link to="/kesfet" data-testid="empty-see-all"
          className="bg-white text-slate-900 border border-slate-200 hover:border-slate-900 px-5 py-2.5 rounded-full font-medium text-sm transition-colors">
          Tüm ilanları gör
        </Link>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="rounded-2xl bg-white premium-border py-20 px-6 text-center" data-testid="explore-error-state">
      <p className="text-sm text-[#B54037] mb-6">{message || "İlanlar yüklenemedi."}</p>
      <button onClick={onRetry} data-testid="explore-retry-button" className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-black transition-colors">
        <ArrowClockwise size={14} weight="bold" /> Tekrar dene
      </button>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <h3 className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-3">{label}</h3>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function FilterBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-3 py-2 text-sm font-medium rounded-lg transition-all ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}
